import { Navigate, useLocation } from "react-router-dom";
import { ShieldCheck, Users, Wine as WineIcon, BarChart3, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import AdminWinesTab from "@/components/admin/AdminWinesTab";
import AdminUsersTab from "@/components/admin/AdminUsersTab";
import AdminStatsTab from "@/components/admin/AdminStatsTab";
import AdminPendingTab from "@/components/admin/AdminPendingTab";

const ADMIN_EMAIL = "alfredwinescom@gmail.com";

export default function Admin() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-white/30 font-body">Loading...</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (user.email?.toLowerCase() !== ADMIN_EMAIL) return <AccessDenied />;

  return (
    <main className="pt-24 pb-20 min-h-screen" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6">
        <AdminHeader />
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-wine-paper border border-white/5 rounded-sm p-1 mb-8">
            <TabTrigger value="pending" testId="tab-pending" icon={Clock}>Pending</TabTrigger>
            <TabTrigger value="wines" testId="tab-wines" icon={WineIcon}>Wines</TabTrigger>
            <TabTrigger value="users" testId="tab-users" icon={Users}>Users</TabTrigger>
            <TabTrigger value="stats" testId="tab-stats" icon={BarChart3}>Stats</TabTrigger>
          </TabsList>
          <TabsContent value="pending"><AdminPendingTab /></TabsContent>
          <TabsContent value="wines"><AdminWinesTab /></TabsContent>
          <TabsContent value="users"><AdminUsersTab currentUserId={user.id} /></TabsContent>
          <TabsContent value="stats"><AdminStatsTab /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function AdminHeader() {
  return (
    <div className="mb-10 flex items-center gap-3">
      <ShieldCheck className="w-5 h-5 text-gold" />
      <div>
        <p className="font-accent text-xs tracking-[0.3em] text-gold/60">ADMIN</p>
        <h1 className="font-display text-3xl text-white/90">Control Panel</h1>
      </div>
    </div>
  );
}

function TabTrigger({ value, testId, icon: Icon, children }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={testId}
      className="font-body text-sm data-[state=active]:bg-merlot/20 data-[state=active]:text-white gap-2"
    >
      <Icon className="w-4 h-4" /> {children}
    </TabsTrigger>
  );
}

function AccessDenied() {
  return (
    <main className="pt-24 pb-20 min-h-screen" data-testid="admin-forbidden">
      <div className="max-w-[600px] mx-auto px-6 text-center">
        <ShieldCheck className="w-10 h-10 text-merlot mx-auto mb-4" />
        <h1 className="font-display text-2xl text-white/90 mb-2">Access Denied</h1>
        <p className="font-body text-sm text-white/40">This area is restricted to administrators.</p>
      </div>
    </main>
  );
}
